import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type {
  CommandPlannerHostStatus,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  RuntimeOutage,
  RuntimeProof,
} from '../lib/data';
import { useCommandPlannerHost } from '../lib/mutations';
import {
  cleanPlannerHostDraft,
  plannerHostConfiguredKey,
  plannerHostOptionLabel,
  plannerHostOptions,
  reconcilePlannerHostDraft,
} from '../lib/planner-host-settings';
import '../planner-host-settings.css';

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function runtimeLabel(
  nodeId: string,
  runtimeKey: string,
  proofs: readonly RuntimeProof[],
  offerings: readonly ModelOfferingRef[],
): string {
  const proof = proofs.find((row) => row.node_id === nodeId && row.runtime_key === runtimeKey);
  if (proof?.runtime_label?.trim()) return proof.runtime_label.trim();
  const offering = offerings.find((row) => row.runtime_key === runtimeKey);
  return offering?.provider_label.trim() || runtimeKey;
}

export function PlannerHostSettings({ client, guard, orgId, status, nodes, nodeOfferings, modelOfferings, runtimeProofs, outages, preferredRuntimeKey, nowMs, onChanged }: {
  client: SupabaseClient;
  guard: Guard;
  orgId: string;
  status: CommandPlannerHostStatus | null;
  nodes: readonly NodePresence[];
  nodeOfferings: readonly NodeOffering[];
  modelOfferings: readonly ModelOfferingRef[];
  runtimeProofs: readonly RuntimeProof[];
  outages: readonly RuntimeOutage[];
  preferredRuntimeKey: string | null;
  nowMs: number;
  onChanged: () => Promise<void>;
}) {
  const mutation = useCommandPlannerHost(client);
  const options = useMemo(() => plannerHostOptions({
    nodes, nodeOfferings, modelOfferings, runtimeProofs, outages, preferredRuntimeKey, nowMs,
  }), [nodes, nodeOfferings, modelOfferings, runtimeProofs, outages, preferredRuntimeKey, nowMs]);
  const configuredKey = plannerHostConfiguredKey(status);
  const [draft, setDraft] = useState(() => cleanPlannerHostDraft(status, options));

  useEffect(() => {
    setDraft((current) => reconcilePlannerHostDraft(current, status, options));
  }, [configuredKey, status?.fallback_mode, options]);

  const chosen = options.find((option) => option.key === draft.choice) ?? null;
  const selectedRuntimeLabel = status === null
    ? null
    : runtimeLabel(status.node_id, status.runtime_key, runtimeProofs, modelOfferings);

  const reconcileAfterUncertainWrite = async () => {
    setDraft((current) => ({ ...current, dirty: false }));
    await onChanged();
  };

  const save = () => {
    if (chosen === null || !chosen.connected || !chosen.available) return;
    // set_command_planner_host is a direct verified-owner mutation, not one of
    // the receipt-backed M1 guarded inputs. Use an existing owner-step-up gate
    // without adding it to GUARDED_RPC_NAMES.
    void guard('post_owner_command', async () => {
      const outcome = await mutation.save({
        orgId,
        nodeId: chosen.nodeId,
        runtimeKey: chosen.runtimeKey,
        fallbackMode: draft.fallback,
        enabled: true,
      });
      if (outcome === 'saved' || outcome === 'unknown') await reconcileAfterUncertainWrite();
    });
  };

  const clear = () => {
    void guard('post_owner_command', async () => {
      const outcome = await mutation.save({
        orgId,
        nodeId: null,
        runtimeKey: '',
        fallbackMode: draft.fallback,
        enabled: false,
      });
      if (outcome === 'saved' || outcome === 'unknown') await reconcileAfterUncertainWrite();
    });
  };

  return (
    <section className="planner-host-settings stack" aria-label="Command Center Planner Host">
      <p className="planner-host-note" role="note">
        Local Command Center planning is not active yet. This selects the future Planner Host; current planning stays on the existing path until the corrected node bridge is live.
      </p>

      {status === null ? (
        <p className="muted">No Planner Host is selected.</p>
      ) : (
        <dl className="planner-host-status">
          <div><dt>Computer</dt><dd>{status.node_name}</dd></div>
          <div><dt>Runtime</dt><dd>{selectedRuntimeLabel ?? status.runtime_key}</dd></div>
          <div><dt>Connected</dt><dd>{status.connected ? 'Connected' : 'Offline'}</dd></div>
          <div><dt>Runtime offered</dt><dd>{yesNo(status.offered)}</dd></div>
          <div><dt>Runtime available</dt><dd>{yesNo(status.runtime_available)}</dd></div>
          <div><dt>Fallback</dt><dd>{status.fallback_mode === 'api' ? 'API' : 'None'}</dd></div>
        </dl>
      )}

      <form className="planner-host-form stack" onSubmit={(event) => { event.preventDefault(); save(); }}>
        <label className="stack">
          <span>Planner Host computer / runtime</span>
          <select
            aria-label="Planner Host computer and runtime"
            value={draft.choice}
            onChange={(event) => setDraft((current) => ({ ...current, choice: event.target.value, dirty: true }))}
          >
            <option value="">Choose a reported computer/runtime</option>
            {status !== null && !options.some((option) => option.key === configuredKey) && (
              <option value={configuredKey} disabled>
                {status.node_name} · {selectedRuntimeLabel ?? status.runtime_key} · not currently offered
              </option>
            )}
            {options.map((option) => (
              <option key={option.key} value={option.key} disabled={!option.connected || !option.available}>
                {plannerHostOptionLabel(option)}
              </option>
            ))}
          </select>
        </label>

        <label className="stack">
          <span>Fallback mode</span>
          <select
            aria-label="Planner Host fallback mode"
            value={draft.fallback}
            onChange={(event) => setDraft((current) => ({
              ...current,
              fallback: event.target.value as 'none' | 'api',
              dirty: true,
            }))}
          >
            <option value="none">None</option>
            <option value="api">API</option>
          </select>
        </label>

        <small className="muted">This setting is for Command Center planning only. It does not change Worker AI selection.</small>

        <div className="row wrap planner-host-actions">
          <button
            type="submit"
            className="primary"
            disabled={mutation.busy || chosen === null || !chosen.connected || !chosen.available}
          >
            Save Planner Host
          </button>
          {status !== null && <button type="button" disabled={mutation.busy} onClick={clear}>Clear Planner Host</button>}
        </div>
        {mutation.error !== null && <p role="alert">{mutation.error}</p>}
      </form>
    </section>
  );
}
